import React, {useEffect, useState} from "react";
import Logo from "../../assets/Logo";
import { FaLinkedin, FaGithub, FaMedium, FaFigma } from "react-icons/fa";
import "./header.css";

import useWidthWindow from "../../utils/useWidthWindow";
import { useLanguage } from "../../i18n/LanguageContext";
import { motion, AnimatePresence } from 'framer-motion';

const mailAddress = "azatesser@gmail.com"

const socialLinks = [
  {
    id: 1,
    name: "Linkedin",
    icon: <FaLinkedin />,
    link: "https://www.linkedin.com/in/azateser",
    mobileShow: true,
  },
  {
    id: 2,
    name: "Github",
    icon: <FaGithub />,
    link: "https://github.com/azateser",
    mobileShow: true,
  },
  {
    id: 3,
    name: "Medium",
    icon: <FaMedium />,
    link: "https://medium.com/@azatesser",
    mobileShow: false,
  },
  {
    id: 4,
    name: "Figma",
    icon: <FaFigma />,
    link: "",
    mobileShow: false,
  },
];

const navLinks = [
    { id: 0, key: "work", link: "#work" },
    { id: 1, key: "projects", link: "#projects" },
    { id: 2, key: "experiences", link: "#experiences" },
    { id: 3, key: "about", link: "#contact" },
    { id: 4, key: "skills", link: "#skills" },
];

// Scroll header animation variants
const scrollHeaderVariants = {
  hidden: { 
    opacity: 0,
    y: -20,
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

const Header = () => {
  const { t, lang, toggleLang } = useLanguage();
  const width = useWidthWindow();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId.replace('#', ''));
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (<>
    <div className="fixed-header-wrapper">
      {isScrolled && <div className="xl:h-20 h-10"></div>}
      
      {/* Normal Header */}
      {!isScrolled && (
        <motion.header 
          id="header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="h-[54px] max-w-default md:px-8 px-6 m-auto flex items-center justify-between py-10"
        >
          <div className="flex items-center md:gap-6 gap-4">
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.2 }}
            >
              <Logo color="#fff" width={width < 768 ? 36 : 56} height="auto" />
            </motion.div>
            <ul className="flex items-center md:gap-2 gap-0 md:text-3xl text-2xl text-[#bdbdbd]">
              {socialLinks.map((item, index) => (
                <motion.li 
                  key={item.id} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                  whileHover={{ scale: 1.1 }}
                  className={`lg:flex ${item.mobileShow ? 'flex' : 'hidden'} items-center justify-center w-12 h-12 opacity-50 hover:opacity-100 cursor-pointer transition-all rounded-md hover:bg-[#201f30]`}
                >
                  <a href={item.link} target="_blank" rel="noopener noreferrer" aria-label={`Azat ESER on ${item.name}`} title={item.name}>{item.icon}</a>
                </motion.li>
              ))}
            </ul>
          </div>

          <nav aria-label="Main">
            <ul className="flex items-center justify-center">
                {navLinks.map((item, index) => (
                    <motion.a 
                      href={item.link} 
                      key={item.id} 
                      onClick={(e) => handleScroll(e, item.link)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 + (0.1 * index), duration: 0.3 }}
                      whileHover={{ opacity: 1 }}
                      className="lg:block hidden text-sm text-textColor/50 opacity-50 hover:text-textColor hover:opacity-100 rounded-md hover:bg-[#201f30] cursor-pointer px-5 py-3 transition-all nav-link"
                    >
                      {t.nav[item.key]}
                    </motion.a>
                ))}
                <li className="mr-1 list-none">
                  <button
                    onClick={toggleLang}
                    aria-label={t.switchTo}
                    title={t.switchTo}
                    className="flex items-center gap-1 rounded-md border border-[rgba(255,255,255,0.12)] px-2.5 py-2 text-xs font-semibold text-[rgba(245,245,245,0.6)] transition-all hover:border-[rgba(255,255,255,0.28)] hover:bg-[#201f30] hover:text-white"
                  >
                    <span className={lang === "en" ? "text-white" : ""}>EN</span>
                    <span className="opacity-30">/</span>
                    <span className={lang === "tr" ? "text-white" : ""}>TR</span>
                  </button>
                </li>
                <motion.li 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  whileHover={{ scale: 1.03 }}
                  className="cursor-pointer md:scale-100 scale-[.85] md:translate-x-0 translate-x-5"
                >
                  <button className="border-gradient group-hover:border-gradient before:transition-animation relative inline-flex items-center rounded-lg border-2 border-solid border-transparent px-5 py-3 md:text-sm text-xs font-medium text-white before:absolute before:-inset-4 before:z-[-1] before:rounded-lg before:bg-gradient-to-r before:from-dark before:via-secondary before:to-tertiary before:opacity-0 before:blur-sm hover:before:opacity-75 hover:before:blur-xl focus:outline-none focus:ring-offset-white focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-75 group">
                    <a href={`mailto:${mailAddress}`} className="opacity-50 group-hover:opacity-100 transition-all duration-1000">{mailAddress}</a>
                  </button>
                </motion.li>
            </ul>
          </nav>
        </motion.header>
      )}
      
      {/* Scrolled Header with Animation */}
      <AnimatePresence>
        {isScrolled && (
          <motion.header 
            id="scrolled-header"
            variants={scrollHeaderVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed-header h-[54px] max-w-default md:px-8 px-6 m-auto flex items-center justify-between py-10"
          >
            <div className="flex items-center md:gap-6 gap-4">
              <motion.div
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.2 }}
              >
                <Logo color="#fff" width={width < 768 ? 36 : 56} height="auto" />
              </motion.div>
              <ul className="flex items-center md:gap-2 gap-0 md:text-3xl text-2xl text-[#bdbdbd]">
                {socialLinks.map((item, index) => (
                  <motion.li 
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.05 * index, duration: 0.2 }}
                    whileHover={{ scale: 1.1 }}
                    className={`lg:flex ${item.mobileShow ? 'flex' : 'hidden'} items-center justify-center w-12 h-12 opacity-50 hover:opacity-100 cursor-pointer transition-all rounded-md hover:bg-[#201f30]`}
                  >
                    <a href={item.link} target="_blank" rel="noopener noreferrer" aria-label={`Azat ESER on ${item.name}`} title={item.name}>{item.icon}</a>
                  </motion.li>
                ))}
              </ul>
            </div>

            <nav aria-label="Main">
              <ul className="flex items-center justify-center">
                  {navLinks.map((item, index) => (
                      <motion.a 
                        href={item.link} 
                        key={item.id} 
                        onClick={(e) => handleScroll(e, item.link)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 + (0.05 * index), duration: 0.2 }}
                        whileHover={{ opacity: 1 }}
                        className="lg:block hidden text-sm text-textColor/50 opacity-50 hover:text-textColor rounded-md hover:bg-[#201f30] cursor-pointer px-5 py-3 transition-all nav-link"
                      >
                        {t.nav[item.key]}
                      </motion.a>
                  ))}
                  <li className="mr-1 list-none">
                    <button
                      onClick={toggleLang}
                      aria-label={t.switchTo}
                      title={t.switchTo}
                      className="flex items-center gap-1 rounded-md border border-[rgba(255,255,255,0.12)] px-2.5 py-2 text-xs font-semibold text-[rgba(245,245,245,0.6)] transition-all hover:border-[rgba(255,255,255,0.28)] hover:bg-[#201f30] hover:text-white"
                    >
                      <span className={lang === "en" ? "text-white" : ""}>EN</span>
                      <span className="opacity-30">/</span>
                      <span className={lang === "tr" ? "text-white" : ""}>TR</span>
                    </button>
                  </li>
                  <motion.li 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.2 }}
                    whileHover={{ scale: 1.03 }}
                    className="cursor-pointer md:scale-100 scale-[.85] md:translate-x-0 translate-x-5"
                  >
                    <button className="border-gradient group-hover:border-gradient before:transition-animation relative inline-flex items-center rounded-lg border-2 border-solid border-transparent px-5 py-3 md:text-sm text-xs font-medium text-white before:absolute before:-inset-4 before:z-[-1] before:rounded-lg before:bg-gradient-to-r before:from-dark before:via-secondary before:to-tertiary before:opacity-0 before:blur-sm hover:before:opacity-75 hover:before:blur-xl focus:outline-none focus:ring-offset-white focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-75 group">
                      <a href={`mailto:${mailAddress}`} className="opacity-50 group-hover:opacity-100 transition-all duration-1000">{mailAddress}</a>
                    </button>
                  </motion.li>
              </ul>
            </nav>
          </motion.header>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default Header;
